import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { SUBJECTS, ERROR_TYPES, getSubjectColor } from '../lib/constants'

export default function ErrorBook({ userId }) {
  const [errors, setErrors] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [filter, setFilter] = useState({ subject: '', errorType: '' })

  // 表单状态
  const [form, setForm] = useState({
    subject: '',
    question_text: '',
    error_type: '',
    knowledge_point: '',
    source_exam: '',
  })

  useEffect(() => {
    fetchErrors()
  }, [userId])

  const fetchErrors = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('gaokao_error_questions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (!error) setErrors(data || [])
    setLoading(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.subject || !form.error_type) return

    const { data, error } = await supabase
      .from('gaokao_error_questions')
      .insert({
        user_id: userId,
        date: new Date().toISOString().split('T')[0],
        subject: form.subject,
        question_text: form.question_text,
        error_type: form.error_type,
        knowledge_point: form.knowledge_point,
        source_exam: form.source_exam,
      })
      .select()
      .single()

    if (!error && data) {
      setErrors(prev => [data, ...prev])
      setForm({ subject: '', question_text: '', error_type: '', knowledge_point: '', source_exam: '' })
      setShowForm(false)

      // 同步到知识点表（用于遗复）
      if (form.knowledge_point) {
        await supabase.from('gaokao_knowledge_points').upsert({
          user_id: userId,
          subject: form.subject,
          name: form.knowledge_point,
          mastery_level: 0,
          last_reviewed_at: new Date().toISOString(),
        }, { onConflict: 'user_id,subject,name' })
      }
    }
  }

  const toggleMastered = async (id, current) => {
    const { data } = await supabase
      .from('gaokao_error_questions')
      .update({ mastered: !current })
      .eq('id', id)
      .select()
      .single()

    if (data) setErrors(prev => prev.map(e => e.id === id ? data : e))
  }

  // 过滤后的错题
  const filtered = errors.filter(e => {
    if (filter.subject && e.subject !== filter.subject) return false
    if (filter.errorType && !e.error_type?.startsWith(filter.errorType)) return false
    return true
  })

  // 错因统计
  const errorStats = {}
  errors.forEach(e => {
    const key = e.error_type?.charAt(0) || '其他'
    errorStats[key] = (errorStats[key] || 0) + 1
  })

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">错题本</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? '取消' : '+ 录入错题'}
        </button>
      </div>

      {/* 录入表单 */}
      {showForm && (
        <form onSubmit={handleSubmit} className="card p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">科目 *</label>
              <select
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                className="input"
                required
              >
                <option value="">选择科目</option>
                {SUBJECTS.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">错因分类 *</label>
              <select
                value={form.error_type}
                onChange={e => setForm({ ...form, error_type: e.target.value })}
                className="input"
                required
              >
                <option value="">选择错因</option>
                {ERROR_TYPES.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label">题目内容</label>
              <textarea
                value={form.question_text}
                onChange={e => setForm({ ...form, question_text: e.target.value })}
                className="input"
                rows="2"
                placeholder="题目描述（或拍照上传）"
              />
            </div>
            <div>
              <label className="label">关联知识点</label>
              <input
                type="text"
                value={form.knowledge_point}
                onChange={e => setForm({ ...form, knowledge_point: e.target.value })}
                className="input"
              />
            </div>
            <div>
              <label className="label">来源卷子</label>
              <input
                type="text"
                value={form.source_exam}
                onChange={e => setForm({ ...form, source_exam: e.target.value })}
                className="input"
              />
            </div>
          </div>
          <button type="submit" className="btn-primary mt-4">保存</button>
        </form>
      )}

      {/* 筛选和统计 */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={filter.subject}
            onChange={e => setFilter({ ...filter, subject: e.target.value })}
            className="input w-auto"
          >
            <option value="">全部科目</option>
            {SUBJECTS.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <select
            value={filter.errorType}
            onChange={e => setFilter({ ...filter, errorType: e.target.value })}
            className="input w-auto"
          >
            <option value="">全部错因</option>
            <option value="A">A - 知识类</option>
            <option value="B">B - 思维类</option>
            <option value="C">C - 计算类</option>
            <option value="D">D - 审题类</option>
            <option value="G">G - 习惯类</option>
          </select>
          <span className="text-sm text-ink-500 ml-auto">
            共 {filtered.length} 道
          </span>
        </div>
      </div>

      {/* 错题列表 */}
      {loading ? (
        <div className="text-center py-12 text-ink-500">加载中...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-ink-500">暂无错题记录</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(error => (
            <div key={error.id} className={`card p-4 ${error.mastered ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: getSubjectColor(error.subject) }}
                    >
                      {error.subject}
                    </span>
                    <span className="text-xs text-ink-500">
                      {ERROR_TYPES.find(t => t.id === error.error_type)?.name || error.error_type}
                    </span>
                    {error.source_exam && (
                      <span className="text-xs text-ink-500">{error.source_exam}</span>
                    )}
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{error.question_text || '（无题目内容）'}</p>
                  {error.knowledge_point && (
                    <p className="text-xs text-ink-500 mt-1">知识点：{error.knowledge_point}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleMastered(error.id, error.mastered)}
                  className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                    error.mastered
                      ? 'bg-green-100 text-green-700'
                      : 'bg-ink-100 text-ink-500'
                  }`}
                >
                  {error.mastered ? '已掌握' : '标记掌握'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
